import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerModule } from '@angular/platform-browser';
import { RouteReuseStrategy, RouterModule } from '@angular/router';

import { HTTP_INTERCEPTORS, HttpClientModule, HttpHeaders } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { IonicModule, IonicRouteStrategy, LoadingController } from '@ionic/angular';
import { Apollo, ApolloModule } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppState } from './app.service';
import { ChecklistService } from './checklist/checklist.service';
import { ClientService } from './client/client.service';
import { ContentSurveyService } from './content-survey/content-survey.service';
import { CordovaService } from './cordova.service';
import { DocumentService } from './document/document.service';
import { HabitService } from './habit/habit.service';
import { I18nPipe } from './i18n.pipe';
import { MarkdownPipe } from './markdown.pipe';
import { I18n } from './i18n.service';
import { NamesPipe } from './names.pipe';
import { NotificationService } from './notification/notification.service';
import { HomePageService } from './pages/home/home-page.service';
import { PathGroupService } from './path-group/path-group.service';
import { PathService } from './path/path.service';
import { PollService } from './poll/poll.service';
import { QuestionService } from './question/question.service';
import { ReplyService } from './reply/reply.service';
import { RewardListComponent } from './reward/reward-list.component';
import { RewardService } from './reward/reward.service';
import { SessionService } from './session/session.service';
import { SurveyService } from './survey/survey.service';
import { ThemeService, ThemeServiceFactory } from './theme/theme.service';
import { TranslationService } from './translation/translation.service';
import { UserService } from './user/user.service';
import { WalletService } from './wallet/wallet.service';

import { onError } from "@apollo/client/link/error";
import { environment } from 'src/environments/environment';
import { AppConstantsService } from './app-constants.service';
import { RoutineService } from './daily-content-v2/routine.service';
import { SingleSignOnService } from './single-sign-on.service';
import { WINDOW_PROVIDERS } from './window.service';
// import { InMemoryCache } from '@apollo/client/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ApolloLink, InMemoryCache } from "@apollo/client/core";
import { AppVersion } from '@awesome-cordova-plugins/app-version/ngx';
import { Calendar } from '@awesome-cordova-plugins/calendar/ngx';
import { Camera } from '@awesome-cordova-plugins/camera/ngx';
import { Chooser } from '@awesome-cordova-plugins/chooser/ngx';
import { FilePath } from '@awesome-cordova-plugins/file-path/ngx';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { FirebaseX } from '@awesome-cordova-plugins/firebase-x/ngx';
import { WebView } from '@awesome-cordova-plugins/ionic-webview/ngx';
import { Keyboard } from '@awesome-cordova-plugins/keyboard/ngx';
import { Market } from '@awesome-cordova-plugins/market/ngx';
import { SocialSharing } from '@awesome-cordova-plugins/social-sharing/ngx';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { TextToSpeechAdvanced } from '@awesome-cordova-plugins/text-to-speech-advanced/ngx';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { StreamingMedia } from '@ionic-native/streaming-media/ngx';
import { MentionModule } from 'angular-mentions';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { FileUploadModule } from 'ng2-file-upload';
import { Ng2SearchPipeModule } from 'ng2-search-filter';
import { CookieService } from 'ngx-cookie-service';
import { ToastrModule } from 'ngx-toastr';
import { SwiperModule } from 'swiper/angular';
import { AlertActionCompleteComponent } from './alert-action-complete/alert-action-complete.component';
import { AlertAddNotesComponent } from './alert-add-notes/alert-add-notes.component';
import { AlertAnimationComponent } from './alert-animation/alert-animation.component';
import { AlertAskRoutineComponent } from './alert-ask-routine/alert-ask-routine.component';
import { AlertCommitCalenderComponent } from './alert-commit-calender/alert-commit-calender.component';
import { AlertCommitRoutineComponent } from './alert-commit-routine/alert-commit-routine.component';
import { AlertConfirmNotificationComponent } from './alert-confirm-notification/alert-confirm-notification.component';
import { AlertDeletePostComponent } from './alert-delete-post/alert-delete-post.component';
import { AlertEditLevelsComponent } from './alert-edit-levels/alert-edit-levels.component';
import { AlertEditNoteComponent } from './alert-edit-note/alert-edit-note.component';
import { AlertGiveFeedbackCommitmentComponent } from './alert-give-feedback-commitment/alert-give-feedback-commitment.component';
import { AlertHabitLoopInfoComponent } from './alert-habit-loop-info/alert-habit-loop-info.component';
import { AlertIamInComponent } from './alert-iam-in/alert-iam-in.component';
import { AlertNotificationUpdateComponent } from './alert-notification-update/alert-notification-update.component';
import { AlertNotifyComponent } from './alert-notify/alert-notify.component';
import { AlertOptRoutineComponent } from './alert-opt-routine/alert-opt-routine.component';
import { AlertProfilePictureComponent } from './alert-profile-picture/alert-profile-picture.component';
import { AlertRatingRoutineComponent } from './alert-rating-routine/alert-rating-routine.component';
import { AlertRepeatEventComponent } from './alert-repeat-event/alert-repeat-event.component';
import { AlertReviewRoutineComponent } from './alert-review-routine/alert-review-routine.component';
import { AlertRoutineAnimComponent } from './alert-routine-anim/alert-routine-anim.component';
import { AlertSupportComponent } from './alert-support/alert-support.component';
import { AlertUserLikeListComponent } from './alert-user-like-list/alert-user-like-list.component';
import { AlertWebCalendarComponent } from './alert-web-calendar/alert-web-calendar.component';
import { AlertWebactionCalendarComponent } from './alert-webaction-calendar/alert-webaction-calendar.component';
import { AllPathsComponent } from './all-paths/all-paths.component';
import { AuthGuardService } from './auth/auth-guard.service';
import { AuthInterceptor } from './auth/auth.interceptor';
import { AuthService, SessionInitializerFactory } from './auth/auth.service';
import { AvatarComponent } from './avatar/avatar.component';
import { ChangeSubpathAlertComponent } from './change-subpath-alert/change-subpath-alert.component';
import { ChatBoardComponent } from './chat-board/chat-board.component';
import { CloseoutSurveyWelcomeComponent } from './closeout-survey/closeout-survey-welcome.component';
import { CloseoutSurveyComponent } from './closeout-survey/closeout-survey.component';
import { CohortService } from './cohorts/cohort.service';
import { CommentListModalComponent } from './comment-list-modal/comment-list-modal.component';
import { CommentComponent } from './comment/comment.component';
import { CommonHeaderComponent } from './common-header/common-header.component';
import { UtilityService } from './common-services/utility.service';
import { CompletedReviewModalComponent } from './completed-review-modal/completed-review-modal.component';
import { FeedbackFormComponent } from './components/commitments/feedback-form/feedback-form.component';
import { ActionCommunityComponent } from './components/community/action-community/action-community.component';
import { HabitCommunityComponent } from './components/community/habit-community/habit-community.component';
import { PostCommunityComponent } from './components/community/post-community/post-community.component';
import { QuestionCommunityComponent } from './components/community/question-community/question-community.component';
import { EditProfileComponent } from './components/edit-profile/edit-profile.component';
import { UserPublicProfileComponent } from './components/user-public-profile/user-public-profile.component';
import { ContentSurveyComponent } from './content-survey/content-survey.component';
import { CustomHammerConfig } from './custom-hammer-config';
import { DailyContentV2Component } from './daily-content-v2/daily-content-v2.component';
import { DailyParticipationService } from './daily-participation/daily-participation.service';
import { DailyStepsService } from './daily-steps/daily-steps.service';
import { DayPartSelectComponent } from './day-part-select/day-part-select.component';
import { DocumentV2Component } from './document-v2/document-v2.component';
import { ErrorhandlingService } from './error-handler';
import { FaqComponent } from './faq/faq.component';
import { ForceRankComponent } from './force-rank/force-rank.component';
import { FullScreenAudioComponent } from './full-screen-audio/full-screen-audio.component';
import { FullScreenFeedComponent } from './full-screen-feed/full-screen-feed.component';
import { FullScreenPostComponent } from './full-screen-post/full-screen-post.component';
import { FullScreenRoutineComponent } from './full-screen-routine/full-screen-routine.component';
import { FullScreenVideoComponent } from './full-screen-video/full-screen-video.component';
import { HabitV2Component } from './habit-v2/habit-v2.component';
import { ImageUploaderComponent } from './image-uploader/image-uploader.component';
import { LeaderboardCardComponent } from './leaderboard-card/leaderboard-card.component';
import { LinearScaleComponent } from './linear-scale/linear-scale.component';
import { MobileWeldonescreenComponent } from './mobile-weldonescreen/mobile-weldonescreen.component';
import { MyCommitmentsComponent } from './my-commitments/my-commitments.component';
import { FullScreenDocumentComponent } from './my-library/full-screen-document.component';
import { FullScreenStepComponent } from './my-library/full-screen-step.component';
import { LibraryService } from './my-library/library.service';
import { MyLibraryComponent } from './my-library/my-library.component';
import { NotificationTimeSelectComponent } from './notification-time-select/notification-time-select.component';
import { OnboardingSurveyComponent } from './onboarding-survey/onboarding-survey.component';
import { AchievementsPageComponent } from './pages/achievement/achievements-page.component';
import { HomeV5Service } from './pages/home-v5/home-v5.service';
import { JournalService } from './pages/journal/journal.service';
import { MobileChatBoardComponent } from './pages/mobile-chat-board/mobile-chat-board.component';
import { MobileChatServiceService } from './pages/mobile-chat-board/mobile-chat-service.service';
import { NotificationsSettingsComponent } from './pages/notifications-settings/notifications-settings.component';
import { PathSelectComponent } from './path-select/path-select.component';
import { PictureUploaderComponent } from './picture-uploader/picture-uploader.component';
import { PollV2Component } from './poll-v2/poll-v2.component';
import { PostFeedComponent } from './post-feed/post-feed.component';
import { PtrContainerComponent } from './ptr-container/ptr-container.component';
import { QuestionV2Component } from './question-v2/question-v2.component';
import { QuestionListComponent } from './question/question-list.component';
import { QuestionComponent } from './question/question.component';
import { RatingAlertBoxComponent } from './rating-alert-box/rating-alert-box.component';
import { ReplyComponent } from './reply/reply.component';
import { HabitLoopComponent } from './routine-v2/habit-loop.component';
import { MeetCueComponent } from './routine-v2/meet-cue.component';
import { RoutineCalendarComponent } from './routine-v2/routine-calendar/routine-calendar/routine-calendar.component';
import { ActivityComponent } from './routine-v2/routine-options/activity.component';
import { DayTimeComponent } from './routine-v2/routine-options/day-time.component';
import { LocationComponent } from './routine-v2/routine-options/location.component';
import { OtherComponent } from './routine-v2/routine-options/other.component';
import { PersonComponent } from './routine-v2/routine-options/person.component';
import { RoutineSubLevelComponent } from './routine-v2/routine-sublevel.component';
import { RoutineV2Component } from './routine-v2/routine-v2.component';
import { RoutineV2Service } from './routine-v2/routine-v2.service';
import { WeekendCalendarComponent } from './routine-v2/weekend-calendar.component';
import { SafeHtml } from './safehtml.pipe';
import { ScreenProgressComponent } from './screen-progress/screen-progress.component';
import { AttachmentUploaderComponent } from './shared/attachment-uploader/attachment-uploader.component';
import { MtrToggleComponent } from './shared/mtr-toggle/mtr-toggle.component';
import { YaFileUploaderComponent } from './shared/ya-file-uploader/ya-file-uploader.component';
import { SignInComponent } from './sign-in/sign-in.component';
import { SignUpComponent } from './sign-up/sign-up.component';
import { SpinnerService } from './spinner.service';
import { StatListComponent } from './stat/stat-list.component';
import { StatComponent } from './stat/stat.component';
import { StatService } from './stat/stat.service';
import { SupportBubbleComponent } from './support-bubble/support-bubble.component';
import { SurveyForceRankComponent } from './survey-force-rank/survey-force-rank.component';
import { SurveyLinearScaleComponent } from './survey-linear-scale/survey-linear-scale.component';
import { SurveyNpsComponent } from './survey-nps/survey-nps.component';
import { TextScreenComponent } from './text-screen/text-screen.component';
import { TimePickerComponent } from './time-picker/time-picker.component';
import { ToasterService } from './toaster.service';
import { UserComponent } from './user/user.component';
import { WebFaqComponent } from './web-faq/web-faq.component';
import { WeldonescreenComponent } from './weldonescreen/weldonescreen.component';
// import { AngMusicPlayerModule } from 'ang-music-player';
import { Badge } from '@awesome-cordova-plugins/badge/ngx';
import { Deeplinks } from '@awesome-cordova-plugins/deeplinks/ngx';
import { DocumentViewer } from '@awesome-cordova-plugins/document-viewer/ngx';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import { FileTransfer } from '@awesome-cordova-plugins/file-transfer/ngx';
import { YoutubeVideoPlayer } from '@awesome-cordova-plugins/youtube-video-player/ngx';
import { EmailComposer } from '@ionic-native/email-composer/ngx';
import mixpanel from 'mixpanel-browser';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { AlertCreateAccountComponent } from './alert-create-account/alert-create-account.component';
import { AlertDeleteReflectionComponent } from './alert-delete-reflection/alert-delete-reflection.component';
import { AlertSelectPathComponent } from './alert-select-path/alert-select-path.component';
import { AlertUnlockLevelComponent } from './alert-unlock-level/alert-unlock-level.component';
import { AlertWrongCodeComponent } from './alert-wrong-code/alert-wrong-code.component';
import { CommentsListWebComponent } from './components/commitments/comments-list-web/comments-list-web.component';
import { HabitCommentListWebComponent } from './components/commitments/habit-comment-list-web/habit-comment-list-web.component';
import { DeletionPageComponent } from './deletion-page/deletion-page.component';
import { DynamicCircleComponent } from './dynamic-circle/dynamic-circle.component';
import { FullTodoInfoComponent } from './full-todo-info/full-todo-info.component';
import { InstepRepliesComponent } from './instep-replies/instep-replies.component';
import { FullScreenReflectionComponent } from './my-library/full-screen-reflection/full-screen-reflection.component';
import { ReflectionService } from './my-library/full-screen-reflection/reflection.service';
import { DailyStepsContentModalComponent } from './new-design/daily-steps-content-modal/daily-steps-content-modal.component';
import { DailyStepsTrackerNewComponent } from './new-design/daily-steps-tracker-new/daily-steps-tracker-new.component';
import { FooziAiConciergeService } from './new-design/foozi-ai-concierge.service';
import { FooziAiConciergeComponent } from './new-design/foozi-ai-concierge/foozi-ai-concierge.component';
import { FooziAiLibraryPopupComponent } from './new-design/foozi-ai-library-popup/foozi-ai-library-popup.component';
import { HamburgerMenuComponent } from './new-design/hamburger-menu/hamburger-menu.component';
import { HomeSheetModalComponent } from './new-design/home-sheet-modal/home-sheet-modal.component';
import { HomeUiService } from './new-design/home-ui.service';
import { HomeWeeklyTrackerComponent } from './new-design/home-weekly-tracker/home-weekly-tracker.component';
import { NewDesignModule } from './new-design/new-design.module';
import { UpdatedHomeComponent } from './new-design/updated-home/updated-home.component';
import { YourPathComponent } from './new-design/your-path/your-path.component';
import { YourProgressComponent } from './new-design/your-progress/your-progress.component';
import { NotificationConfirmationPopupComponent } from './notification-confirmation-popup/notification-confirmation-popup.component';
import { OnboardingLearningNotificationComponent } from './onboarding-learning-notification/onboarding-learning-notification.component';
import { QuestionCommentComponent } from './question-comment/question-comment.component';
import { ReflectionComponent } from './reflection/reflection.component';
import { ReversePipe } from './reverse.pipe';
import { RewardComponent } from './reward/reward.component';
import { NativeService } from './services/native.service';
import { AnimationsService } from './shared/animations.service';
import { PopoverComponent } from './shared/popover/popover.component';
import { SkeletonListComponent } from './shared/skeleton-list/skeleton-list.component';
import { SignupPopoverComponent } from './signup-popover/signup-popover.component';
import { SurveyMcqComponent } from './survey/survey-mcq/survey-mcq.component';
import { ActionRescheduleComponent } from './to-do-list/action-reschedule/action-reschedule.component';
import { ToDoListComponent } from './to-do-list/to-do-list.component';
// import { ScreenOrientation } from "@awesome-cordova-plugins/screen-orientation/ngx";
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx';
import { BranchIo } from '@awesome-cordova-plugins/branch-io/ngx';
import { BrowserTab } from '@ionic-native/browser-tab/ngx';
import { Clipboard } from '@ionic-native/clipboard/ngx';
import { IosASWebauthenticationSession } from "@ionic-native/ios-aswebauthenticationsession-api/ngx";
import { ScreenOrientation } from "@ionic-native/screen-orientation/ngx";
import { PlyrModule } from '@makiwin/ngx-plyr';
import { ActionHubComponent } from './action-hub/action-hub.component';
import { EosWeeklyCalendarV1Component } from './new-design/eos-weekly-calendar-v1/eos-weekly-calendar-v1.component';
import { ReoptHabitLevelModalComponent } from './reopt-habit-level-modal/reopt-habit-level-modal.component';
import { StripHtmlPipe } from './strip-html.pipe';
import { WrongLoginLinkComponent } from './pages/wrong-login-link/wrong-login-link.component';
import { TranslateComponentModalComponent } from './components/translate-component-modal/translate-component-modal.component';
import { ChapterStepsModalComponent } from './new-design/chapter-steps-modal/chapter-steps-modal.component';
import { HabitDescriptionModalComponent } from './new-design/habit-description-modal/habit-description-modal.component';
import { YourPathNewComponent } from './new-design/your-path-new/your-path-new.component';
import { ChapterCompleteScreenComponent } from './new-design/chapter-complete-screen/chapter-complete-screen.component';
import { DailyStepsContentWebModalV2Component } from './new-design/daily-steps-content-web-modal-v2/daily-steps-content-web-modal-v2.component';
import { DailyStepsContentMobileV2Component } from './new-design/daily-steps-content-mobile-v2/daily-steps-content-mobile-v2.component';
import { FooterNavigationComponent } from './footer-navigation/footer-navigation.component';
import { HeaderProgressBarComponent } from './header-progress-bar/header-progress-bar.component';
import { KyperDataApiService } from './services/kyper-data-api.service';
import { MediaTypesScreenComponent } from './media-types-screen/media-types-screen.component';
import { AllPathsV2Component } from './all-paths-v2/all-paths-v2.component';
import { AlertPathSelectV2Component } from './alert-path-select-v2/alert-path-select-v2.component';
import { MicroActionChatbotV1Component } from './micro-action-chatbot-v1/micro-action-chatbot-v1.component';
import { PracticeBotModalComponent } from './new-design/practice-bot-modal/practice-bot-modal.component';
import { FooziHistoryModalComponent } from './new-design/foozi-history-modal/foozi-history-modal.component';
import { MicroActionHistoryModalComponent } from './new-design/micro-action-history-modal/micro-action-history-modal.component';
import { PracticeVoiceCallService } from './new-design/practice-voice-call.service';
import { PracticeBotService } from './new-design/practice-bot.service';
@NgModule({
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA,
    NO_ERRORS_SCHEMA
  ],
  declarations: [
    ReversePipe,
    NamesPipe,
    RewardListComponent,
    FullScreenAudioComponent,
    FullScreenVideoComponent,
    AppComponent,
    SignInComponent,
    SignUpComponent,
    FaqComponent,
    SurveyMcqComponent,
    PictureUploaderComponent,
    OnboardingSurveyComponent,
    UpdatedHomeComponent, 
    HomeSheetModalComponent,
    I18nPipe,
    MarkdownPipe,
    SafeHtml,
    TimePickerComponent,
    LinearScaleComponent,
    ImageUploaderComponent,
    UserComponent,
    HabitCommentListWebComponent,
    ScreenProgressComponent,
    SkeletonListComponent,
    AlertNotificationUpdateComponent,
    AlertReviewRoutineComponent,
    AlertCommitCalenderComponent,
    AlertWebCalendarComponent,
    AlertAnimationComponent,
    AlertConfirmNotificationComponent,
    ChangeSubpathAlertComponent,
    AlertWebactionCalendarComponent,
    AlertRatingRoutineComponent,
    AlertIamInComponent,
    StatListComponent,
    StatComponent,
    AvatarComponent,
    MyCommitmentsComponent,
    MyLibraryComponent,
    ChangeSubpathAlertComponent,
    AlertNotifyComponent,
    AlertIamInComponent,
    AlertRatingRoutineComponent,
    AlertUserLikeListComponent,
    AlertProfilePictureComponent,
    AlertWebCalendarComponent,
    AlertWebactionCalendarComponent,
    AlertSupportComponent,
    AlertActionCompleteComponent,
    AlertAskRoutineComponent,
    AlertHabitLoopInfoComponent,
    AlertGiveFeedbackCommitmentComponent,
    AlertEditLevelsComponent,
    AlertNotificationUpdateComponent,
    AlertReviewRoutineComponent,
    AlertAnimationComponent,
    AlertConfirmNotificationComponent,
    RoutineCalendarComponent,
    AlertCommitRoutineComponent,
    AlertOptRoutineComponent,
    AlertUserLikeListComponent,
    AlertRepeatEventComponent,
    AlertRoutineAnimComponent,
    WeekendCalendarComponent,
    AchievementsPageComponent,
    QuestionComponent,
    QuestionListComponent,
    ChatBoardComponent,
    MobileChatBoardComponent,
    ReplyComponent,
    CommentComponent,
    AttachmentUploaderComponent,
    FullScreenFeedComponent,
    FullScreenPostComponent,
    FullScreenRoutineComponent,
    AlertRepeatEventComponent,
    AlertRoutineAnimComponent,
    WeekendCalendarComponent,
    DailyContentV2Component,
    DailyStepsContentModalComponent,
    YaFileUploaderComponent,
    DocumentV2Component,
    TextScreenComponent,
    EditProfileComponent,
    HabitV2Component,
    PollV2Component,
    ForceRankComponent,
    ContentSurveyComponent,
    QuestionV2Component,
    ReplyComponent,
    ActivityComponent,
    DayTimeComponent,
    LocationComponent,
    OtherComponent,
    PersonComponent,
    HabitLoopComponent,
    MeetCueComponent,
    RoutineSubLevelComponent,
    RoutineV2Component,
    AttachmentUploaderComponent,
    WeldonescreenComponent,
    MobileWeldonescreenComponent,
    SupportBubbleComponent,
    AlertSupportComponent,
    PathSelectComponent,
    CloseoutSurveyComponent,
    CloseoutSurveyWelcomeComponent,
    NotificationsSettingsComponent,
    RatingAlertBoxComponent,
    PtrContainerComponent,
    SurveyLinearScaleComponent,
    SurveyForceRankComponent,
    SurveyNpsComponent,
    PostFeedComponent,
    NotificationTimeSelectComponent,
    AllPathsComponent,
    MtrToggleComponent,
    CommentListModalComponent,
    CompletedReviewModalComponent,
    FeedbackFormComponent,
    HabitCommunityComponent,
    ActionCommunityComponent,
    QuestionCommunityComponent,
    CommentsListWebComponent,
    PostCommunityComponent,
    LeaderboardCardComponent,
    DayPartSelectComponent,
    WebFaqComponent,
    CommonHeaderComponent,
    UserPublicProfileComponent,
    AlertAddNotesComponent,
    FullScreenStepComponent,
    FullScreenDocumentComponent,
    AlertEditNoteComponent,
    AlertDeletePostComponent,
    DeletionPageComponent,
    ToDoListComponent,
    FullTodoInfoComponent,
    ActionRescheduleComponent,
    AlertUnlockLevelComponent,
    InstepRepliesComponent,
    RewardComponent,
    QuestionCommentComponent,
    DynamicCircleComponent,
    ReflectionComponent,
    PopoverComponent,
    WrongLoginLinkComponent,
    FullScreenReflectionComponent,
    AlertDeleteReflectionComponent,
    AlertCreateAccountComponent,
    YourProgressComponent,
    YourPathComponent,
    YourPathNewComponent,
    ChapterCompleteScreenComponent,
    DailyStepsTrackerNewComponent,
    HamburgerMenuComponent,
    HomeWeeklyTrackerComponent,
    DailyStepsContentModalComponent,
    AlertWrongCodeComponent,
    SignupPopoverComponent,
    AlertWrongCodeComponent,
    AlertPathSelectV2Component,
    FooziAiConciergeComponent,
    FooziAiLibraryPopupComponent,
    OnboardingLearningNotificationComponent,
    NotificationConfirmationPopupComponent,
    ReoptHabitLevelModalComponent,
    StripHtmlPipe,
    ActionHubComponent,
    EosWeeklyCalendarV1Component,
    ChapterStepsModalComponent,
    HabitDescriptionModalComponent,
    TranslateComponentModalComponent,
    DailyStepsContentMobileV2Component,
    DailyStepsContentWebModalV2Component,
    FooterNavigationComponent,
    HeaderProgressBarComponent,
    MediaTypesScreenComponent,
    AllPathsV2Component,
    MicroActionChatbotV1Component,
    PracticeBotModalComponent,
    FooziHistoryModalComponent,
    MicroActionHistoryModalComponent
  ],
  imports: [
    // AngMusicPlayerModule,
    CommonModule,
    BrowserModule,
    IonicModule.forRoot(),
    AppRoutingModule,
    ApolloModule,
    NgxExtendedPdfViewerModule,
    // HttpLinkModule,
    HttpClientModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    FileUploadModule,
    // AngularSvgIconModule,
    MatDialogModule,
    MatRadioModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatRippleModule,
    MentionModule,
    // MatProgressSpinnerModule,
    MatCheckboxModule,
    MatInputModule,
    MatMenuModule,
    MatExpansionModule,
    MatBottomSheetModule,
    MatDividerModule,
    MatSidenavModule,
    MatListModule,
    MatTooltipModule,
    MatTabsModule,
    MatSliderModule,
    // NgxSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatSlideToggleModule,
    MatNativeDateModule,
    HammerModule,
    SwiperModule,
    Ng2SearchPipeModule,
    ToastrModule.forRoot({
      timeOut: 3000
    }),
    // NgxLinkifyjsModule.forRoot(),
    NgCircleProgressModule.forRoot({}),
    RouterModule.forRoot([]),
    PlyrModule,
  ],
  providers: [
    YoutubeVideoPlayer,
    StreamingMedia,
    LoadingController,
    DatePipe,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    AppState,
    Clipboard,
    BranchIo,
    BrowserTab,
    ThemeService,
    {
      provide: APP_INITIALIZER,
      useFactory: ThemeServiceFactory,
      deps: [ThemeService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: SessionInitializerFactory,
      deps: [AuthService],
      multi: true,
    },
    AuthGuardService,
    AuthService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: CustomHammerConfig
    },
    { provide: ErrorHandler, useClass: ErrorhandlingService },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'fill' }
    },
    SessionService,
    CookieService,
    HomePageService,
    DocumentService,
    PathGroupService,
    PathService,
    ChecklistService,
    HabitService,
    UserService,
    ClientService,
    QuestionService,
    ReplyService,
    RewardListComponent,
    RewardComponent,
    RewardService,
    HomeV5Service,
    StatService,
    DailyParticipationService,
    PollService,
    ContentSurveyService,
    JournalService,
    SpinnerService,
    WalletService,
    TranslationService,
    SurveyService,
    NotificationService,
    CordovaService,
    DailyStepsService,
    MobileChatServiceService,
    NamesPipe,
    I18n,
    I18nPipe,
    SafeHtml,
    RoutineService,
    SingleSignOnService,
    RoutineV2Service,
    AppConstantsService,
    WINDOW_PROVIDERS,
    MatDatepickerModule,
    MatNativeDateModule,
    ToasterService,
    UtilityService,
    Calendar,
    InAppBrowser,
    StatusBar,
    TextToSpeechAdvanced,
    SocialSharing,
    Camera,
    Deeplinks,
    Keyboard,
    File,
    WebView,
    FilePath,
    Chooser,
    FirebaseX,
    CohortService,
    Market,
    AppVersion,
    LibraryService,
    Badge,
    FileReader,
    FileOpener,
    // DocumentViewer,
    FileTransfer,
    ReflectionService,
    EmailComposer,
    NewDesignModule,
    HomeUiService,
    AnimationsService,
    IosASWebauthenticationSession,
    NativeService,
    FooziAiConciergeService,
    DailyStepsContentMobileV2Component,
    DailyStepsContentWebModalV2Component,
    ScreenOrientation,
    SplashScreen,
    AndroidPermissions,
    KyperDataApiService,
    PracticeVoiceCallService,
    PracticeBotService
  ],
  bootstrap: [AppComponent]
})

export class AppModule {
  constructor(
    private apollo: Apollo,
    private httpLink: HttpLink,
    private appState: AppState,
    private spinnerService: SpinnerService
  ) {
    const http = httpLink.create({ uri: environment.graphQLUrl, withCredentials: true });

    const authMiddleware = new ApolloLink((operation, forward) => {
      // add the authorization to the headers
      const headers = new HttpHeaders();
      headers.set('Authorization', localStorage.getItem('token'));
      if (localStorage.getItem('token')) {
        operation.setContext({
          headers: headers
        });
      }
      return forward(operation);
    });

    const errorHandler = onError((errorResponse) => {
      console.log(errorResponse);
      const { graphQLErrors, networkError } = errorResponse;
      spinnerService.off()
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message }) => {
          console.log(`Graphql error: ${message}`);
          mixpanel.track('GraphQl Error', {graphQLError:message})
          // Handle authentication errors
          if (message && (message.toLowerCase().includes('unauthorized') || 
              message.toLowerCase().includes('invalid token') || 
            message.toLowerCase().includes('token expired') ||
            message.toLowerCase().includes("this app no longer supports 1st90 paths. please download the 1st90 app."))) {
            mixpanel.track('Authentication Error', {errorMessage: message});
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            sessionStorage.clear();
            // Force hard reload to ensure clean state and re-initialization
            window.location.href = '/login';
          }
        });
      }

      if (networkError) {
        console.log(`Network error: ${networkError.message}`);
        mixpanel.track('Network Error', {networkError:networkError.message})
        
        // Handle 401 Unauthorized HTTP error
        if (networkError['status'] === 401) {
          mixpanel.track('401 Unauthorized', {networkError:networkError.message})
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          sessionStorage.clear();
          // Force hard reload to ensure clean state and re-initialization
          window.location.href = '/login';
        }
      }
      // let messages = errorResponse.graphQLErrors.map(e => e.message)
      // toasterService.error(messages.join('<br>'), 'Error', {enableHtml: true})
    });

    // const cache = new InMemoryCache({
    //   dataIdFromObject: object => {
    //     switch (object.__typename) {
    //       case 'foo': return object.key; // use `key` as the primary key
    //       case 'bar': return object.blah; // use `blah` as the priamry key
    //       default: return object.id || object._id
    //     }
    //   }
    // });

    apollo.create({
      link: ApolloLink.from([authMiddleware, errorHandler, http]),
      cache: new InMemoryCache(),
      connectToDevTools: true
    });
    mixpanel.init(environment.mixPanelToken);
  }
}
